import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import i18n, {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  type SupportedLanguage,
} from '@/lib/i18n';
import {
  applyAccent,
  DEFAULT_ACCENT,
  isAccentId,
  type AccentId,
} from '@/lib/branding/accent-themes';

/**
 * Theme modes:
 * - 'light' / 'dark': pinned by the user
 * - 'system': follow `prefers-color-scheme` (default)
 */
export type ThemeMode = 'light' | 'dark' | 'system';

export const DEFAULT_THEME: ThemeMode = 'system';

/**
 * The accent is remembered per brand, not once per operator.
 *
 * The whole request (#229) is "I look after several sites, let each one look
 * different", so the choice has to key off which brand the tab is showing.
 * The key is the organization id, persisted alongside the colours so a cold
 * boot repaints the right one at first paint instead of flickering it in when
 * /organizations/me answers. `ACCENT_ORPHAN_KEY` is the placeholder held before
 * a brand has ever been seen in this browser.
 */
export const ACCENT_ORPHAN_KEY = '_';

interface UiState {
  language: SupportedLanguage;
  theme: ThemeMode;
  /** Concrete theme actually applied to <html> ('light' | 'dark') — derived from `theme` + system. */
  resolvedTheme: 'light' | 'dark';
  sidebarCollapsed: boolean;
  /** Personal accent per brand, keyed by organization id. */
  accentByOrg: Record<string, AccentId>;
  /** Organization id the tab is currently showing, or `ACCENT_ORPHAN_KEY`. */
  accentOrgKey: string;
  /** Accent in force right now — `accentByOrg[accentOrgKey]`, or the default. */
  accent: AccentId;
  setLanguage: (lang: SupportedLanguage) => void;
  setTheme: (theme: ThemeMode) => void;
  /** Point the accent at a brand. Called when /organizations/me resolves. */
  setAccentOrg: (orgId: string | null | undefined) => void;
  /** Choose the accent for the brand this tab is showing. */
  setAccent: (accent: AccentId) => void;
  toggleSidebar: () => void;
  /** Called internally by the system-theme watcher — do not call from UI. */
  _applyResolvedTheme: () => void;
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolve(theme: ThemeMode): 'light' | 'dark' {
  if (theme === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return theme;
}

function applyToDocument(resolved: 'light' | 'dark') {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  document.documentElement.dataset.theme = resolved;
}

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      language: DEFAULT_LANGUAGE,
      theme: DEFAULT_THEME,
      resolvedTheme: resolve(DEFAULT_THEME),
      sidebarCollapsed: false,
      accentByOrg: {},
      accentOrgKey: ACCENT_ORPHAN_KEY,
      accent: DEFAULT_ACCENT,
      setLanguage: (lang) => {
        void i18n.changeLanguage(lang);
        if (typeof document !== 'undefined') {
          document.documentElement.lang = lang;
        }
        set({ language: lang });
      },
      setTheme: (theme) => {
        const resolved = resolve(theme);
        applyToDocument(resolved);
        // The accent ships a light and a dark ramp; flipping mode has to repaint
        // it or a dark screen keeps the light-mode sidebar tint.
        applyAccent(get().accent, resolved);
        set({ theme, resolvedTheme: resolved });
      },
      setAccentOrg: (orgId) => {
        // A missing id means "not known yet", never "no brand" — the query that
        // supplies it is pending on every boot and can fail outright. Treating
        // it as a brand change would throw away the colour just rehydrated.
        if (!orgId) return;
        const { accentByOrg, accentOrgKey, resolvedTheme } = get();
        if (orgId === accentOrgKey) return;
        const accent = accentByOrg[orgId] ?? DEFAULT_ACCENT;
        applyAccent(accent, resolvedTheme);
        set({ accentOrgKey: orgId, accent });
      },
      setAccent: (accent) => {
        const { accentOrgKey, accentByOrg, resolvedTheme } = get();
        applyAccent(accent, resolvedTheme);
        set({ accent, accentByOrg: { ...accentByOrg, [accentOrgKey]: accent } });
      },
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      _applyResolvedTheme: () => {
        const resolved = resolve(get().theme);
        applyToDocument(resolved);
        applyAccent(get().accent, resolved);
        set({ resolvedTheme: resolved });
      },
    }),
    {
      name: 'b1fr0st.ui',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        language: s.language,
        theme: s.theme,
        sidebarCollapsed: s.sidebarCollapsed,
        accentByOrg: s.accentByOrg,
        accentOrgKey: s.accentOrgKey,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        if (typeof document !== 'undefined') {
          document.documentElement.lang = state.language;
        }
        if (i18n.language !== state.language) {
          void i18n.changeLanguage(state.language);
        }
        // Apply the resolved theme + accent through the store so `resolvedTheme`
        // is in sync. Use a microtask so it runs after the persist middleware
        // finishes rehydrating, and go through `setState` rather than mutating
        // the rehydrated object so mounted components are actually notified.
        queueMicrotask(() => {
          const s = useUiStore.getState();
          // A theme dropped by a later release must not paint a half-set of
          // properties, so anything unrecognised falls back to the brand colour.
          const accentByOrg = Object.fromEntries(
            Object.entries(s.accentByOrg ?? {}).filter(([, id]) => isAccentId(id)),
          );
          const accentOrgKey = s.accentOrgKey || ACCENT_ORPHAN_KEY;
          useUiStore.setState({
            accentByOrg,
            accentOrgKey,
            accent: accentByOrg[accentOrgKey] ?? DEFAULT_ACCENT,
          });
          useUiStore.getState()._applyResolvedTheme();
        });
        try {
          localStorage.setItem(LANGUAGE_STORAGE_KEY, state.language);
        } catch {
          /* ignore */
        }
      },
    },
  ),
);

/**
 * Subscribe to the OS-level color-scheme change so `theme: 'system'` stays in sync
 * without a reload. Safe to call multiple times — MediaQueryList dedupes listeners.
 */
export function initSystemThemeWatcher(): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = () => {
    if (useUiStore.getState().theme === 'system') {
      useUiStore.getState()._applyResolvedTheme();
    }
  };
  mql.addEventListener('change', handler);
  return () => mql.removeEventListener('change', handler);
}
